/**
 * Schedule Data Service
 *
 * Supports two data source modes controlled by VITE_DATA_SOURCE:
 *   - 'mock' (default): loads from /mockdata/ JSON files
 *   - 'supabase': queries schedule tables directly via Supabase JS client
 *
 * Drop-in replaceable with OData V4 when ready.
 */

import type {
  ScheduleVersion,
  Activity,
  Relationship,
  Resource,
  ResourceAssignment,
  ScheduleCalendar,
  WbsNode,
  Baseline,
  ActivityCodeType,
  ActivityCodeValue,
  ScheduleStatistics,
} from '@/types/scheduling'
import { supabase } from '@/lib/supabase'
import { saveScheduleVersion } from '@/services/scheduleWriteService'

// ---------------------------------------------------------------------------
// Data source resolution
// ---------------------------------------------------------------------------

function isSupabaseMode(): boolean {
  return (import.meta.env.VITE_DATA_SOURCE as string) === 'supabase'
}

// ---------------------------------------------------------------------------
// Mock-file helpers (unchanged from previous implementation)
// ---------------------------------------------------------------------------

const MOCK_BASE = '/mockdata'
const cache = new Map<string, unknown>()

function getActiveDemoProfileDir(): string {
  try {
    const profileId = localStorage.getItem('ppm-demo-profile') ?? 'technology-digital'
    if (profileId === 'technology-digital' || profileId === 'default') return ''
    const profileDirs: Record<string, string> = {
      'engineering-construction': 'profiles/engineering-construction',
    }
    return profileDirs[profileId] ?? ''
  } catch { return '' }
}

const VERSION_FILE_MAP: Record<string, string> = {
  sv_001: 'schedules/schedule-version-001.json',
  sv_ec_001: 'schedules/schedule-ec-001.json',
  sv_ec_002: 'schedules/schedule-ec-002.json',
  sv_ec_003: 'schedules/schedule-ec-003.json',
  sv_ec_004: 'schedules/schedule-ec-004.json',
  sv_ec_005: 'schedules/schedule-ec-005.json',
  sv_ec_006: 'schedules/schedule-ec-006.json',
  sv_ec_007: 'schedules/schedule-ec-007.json',
  sv_ec_008: 'schedules/schedule-ec-008.json',
}

const PROJECT_VERSION_MAP: Record<string, string> = {
  proj_001: 'schedules/schedule-version-001.json',
}

const EC_PROJECT_VERSION_MAP: Record<string, string> = {
  proj_001: 'schedules/schedule-ec-001.json',
  proj_002: 'schedules/schedule-ec-002.json',
  proj_003: 'schedules/schedule-ec-003.json',
  proj_004: 'schedules/schedule-ec-004.json',
  proj_005: 'schedules/schedule-ec-005.json',
  proj_006: 'schedules/schedule-ec-006.json',
  proj_007: 'schedules/schedule-ec-007.json',
  proj_008: 'schedules/schedule-ec-008.json',
}

function resolveScheduleFile(versionId: string): string {
  return VERSION_FILE_MAP[versionId] ?? 'schedules/schedule-version-001.json'
}

function resolveProjectScheduleFile(projectId: string): string {
  const profileDir = getActiveDemoProfileDir()
  if (profileDir) {
    return EC_PROJECT_VERSION_MAP[projectId] ?? 'schedules/schedule-ec-001.json'
  }
  return PROJECT_VERSION_MAP[projectId] ?? 'schedules/schedule-version-001.json'
}

async function load<T>(file: string): Promise<T> {
  const profileDir = getActiveDemoProfileDir()
  const resolvedFile = profileDir ? `${profileDir}/${file}` : file
  const cacheKey = resolvedFile

  const cached = cache.get(cacheKey)
  if (cached) return cached as T

  if (profileDir) {
    const profileRes = await fetch(`${MOCK_BASE}/${resolvedFile}`)
    if (profileRes.ok) {
      const data = await profileRes.json()
      cache.set(cacheKey, data)
      return data as T
    }
    const fallbackRes = await fetch(`${MOCK_BASE}/${file}`)
    if (!fallbackRes.ok) throw new Error(`Failed to load schedule data: ${file}`)
    const data = await fallbackRes.json()
    cache.set(cacheKey, data)
    return data as T
  }

  const res = await fetch(`${MOCK_BASE}/${resolvedFile}`)
  if (!res.ok) throw new Error(`Failed to load schedule data: ${file}`)
  const data = await res.json()
  cache.set(cacheKey, data)
  return data as T
}

export function clearScheduleCache() {
  cache.clear()
}

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

export interface ScheduleData {
  /**
   * Per-activity updated_at timestamps captured at fetch time.
   * Used as the optimistic-concurrency token for save_schedule_version.
   * Empty in mock mode (saves are not supported there).
   */
  activityUpdatedAt?: Record<string, string>
  version: ScheduleVersion
  calendars: ScheduleCalendar[]
  wbs: WbsNode[]
  activities: Activity[]
  relationships: Relationship[]
  resources: Resource[]
  resourceAssignments: ResourceAssignment[]
  activityCodeTypes: ActivityCodeType[]
  activityCodeValues: ActivityCodeValue[]
  baselines: Baseline[]
  versions: ScheduleVersion[]
}

// ---------------------------------------------------------------------------
// Normalizers
// ---------------------------------------------------------------------------

function normalizeActivities(activities: Activity[]): Activity[] {
  return activities.map(a => ({
    ...a,
    customText: a.customText ?? {},
    customNumber: a.customNumber ?? {},
    customDate: a.customDate ?? {},
    // Milestone classification defaults — null until classified
    milestoneCategory: a.milestoneCategory ?? null,
    customerFacingName: a.customerFacingName ?? null,
    customerCommitmentDate: a.customerCommitmentDate ?? null,
    linkedContractId: a.linkedContractId ?? null,
    linkedContractMilestoneLabel: a.linkedContractMilestoneLabel ?? null,
    hasBillingImpact: a.hasBillingImpact ?? false,
    billingAmount: a.billingAmount ?? null,
    billingCurrency: a.billingCurrency ?? null,
    revenueRecognitionTrigger: a.revenueRecognitionTrigger ?? null,
    lifecyclePhaseTransition: a.lifecyclePhaseTransition ?? null,
    classifiedBy: a.classifiedBy ?? null,
    classifiedAt: a.classifiedAt ?? null,
  }))
}

/**
 * Fill in the version fields added by migration 00464 so mock-mode JSON and
 * pre-migration rows behave like a fresh row rather than yielding `undefined`.
 * `projectStartDate` falls back to the data date, which is what the CPM engine
 * anchored on before the column existed.
 */
function normalizeVersion(v: ScheduleVersion): ScheduleVersion {
  return {
    ...v,
    projectStartDate: v.projectStartDate ?? v.dataDate ?? null,
    isDefault: v.isDefault ?? false,
    archivedAt: v.archivedAt ?? null,
    archivedBy: v.archivedBy ?? null,
  }
}

function normalizeVersions(versions: ScheduleVersion[]): ScheduleVersion[] {
  return versions.map(normalizeVersion)
}

/**
 * Resolve the controlling version for a project: the explicit default, then the
 * Active version, then the first live one. Archived versions never win.
 */
export function resolveDefaultVersion(versions: ScheduleVersion[]): ScheduleVersion | null {
  const live = versions.filter((v) => !v.archivedAt)
  const pool = live.length > 0 ? live : versions
  return (
    pool.find((v) => v.isDefault) ??
    pool.find((v) => v.status === 'Active') ??
    pool[0] ??
    null
  )
}

function normalizeWbs(wbs: WbsNode[]): WbsNode[] {
  return wbs.map(w => ({
    ...w,
    elementType: w.elementType ?? 'Summary',
    responsibleOrg: w.responsibleOrg ?? null,
    budgetAtCompletion: w.budgetAtCompletion ?? null,
    isAgile: w.isAgile ?? false,
  }))
}

// ---------------------------------------------------------------------------
// Supabase helpers — snake_case DB → camelCase TS
// ---------------------------------------------------------------------------

function snakeToCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase())
}

function transformKeys(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(obj)) {
    out[snakeToCamel(k)] = v
  }
  return out
}

function transformRow<T>(row: Record<string, unknown>): T {
  return transformKeys(row) as T
}

function transformRows<T>(rows: Record<string, unknown>[]): T[] {
  return rows.map(r => transformRow<T>(r))
}

async function queryTable<T>(
  table: string,
  filterCol: string,
  filterVal: string,
): Promise<T[]> {
  return queryTableAll<T>(table, q => q.eq(filterCol, filterVal))
}

// PostgREST caps each response at 1000 rows by default. Schedule WBS / activities
// regularly exceed that on real projects, so we page through with explicit ranges.
async function queryTableAll<T>(
  table: string,
  applyFilter: (q: ReturnType<ReturnType<typeof supabase.from>['select']>) => ReturnType<ReturnType<typeof supabase.from>['select']>,
): Promise<T[]> {
  const PAGE = 1000
  const out: Record<string, unknown>[] = []
  let from = 0
  while (true) {
    const base = supabase.from(table).select('*')
    const { data, error } = await applyFilter(base).range(from, from + PAGE - 1)
    if (error) {
      console.warn(`[schedule] Supabase query error [${table}]:`, error.message)
      return transformRows<T>(out)
    }
    const rows = (data ?? []) as unknown as Record<string, unknown>[]
    out.push(...rows)
    if (rows.length < PAGE) break
    from += PAGE
  }
  return transformRows<T>(out)
}

// Same as queryTableAll but returns raw (untransformed) rows so the caller can
// peek at server-side metadata columns like updated_at.
async function queryTableRawAll(
  table: string,
  applyFilter: (q: ReturnType<ReturnType<typeof supabase.from>['select']>) => ReturnType<ReturnType<typeof supabase.from>['select']>,
): Promise<Record<string, unknown>[]> {
  const PAGE = 1000
  const out: Record<string, unknown>[] = []
  let from = 0
  while (true) {
    const base = supabase.from(table).select('*')
    const { data, error } = await applyFilter(base).range(from, from + PAGE - 1)
    if (error) return out
    const rows = (data ?? []) as unknown as Record<string, unknown>[]
    out.push(...rows)
    if (rows.length < PAGE) break
    from += PAGE
  }
  return out
}

// ---------------------------------------------------------------------------
// Supabase schedule version query
// ---------------------------------------------------------------------------

async function sbGetScheduleVersion(versionId: string): Promise<ScheduleVersion | null> {
  // .maybeSingle() returns { data: null, error: null } on zero rows, where
  // .single() returns HTTP 406 — which then surfaces in the browser console
  // even though our caller already handles null gracefully. Same behavior
  // semantically; quieter at the network layer.
  const { data, error } = await supabase
    .from('schedule_versions')
    .select('*')
    .eq('id', versionId)
    .maybeSingle()
  if (error || !data) return null
  return normalizeVersion(transformRow<ScheduleVersion>(data as unknown as Record<string, unknown>))
}

// ---------------------------------------------------------------------------
// Supabase: full schedule load (parallel queries)
// ---------------------------------------------------------------------------

async function sbGetScheduleData(versionId: string): Promise<ScheduleData> {
  // Single version fetch shared by both consumers below — without this, the
  // function fires sbGetScheduleVersion twice in parallel for the same row.
  const versionPromise = sbGetScheduleVersion(versionId)

  const [version, calendars, wbs, activitiesResult, relationships, resources, assignments, baselines, codeTypes, codeValues, versions] =
    await Promise.all([
      versionPromise,
      queryTable<ScheduleCalendar>('schedule_calendars', 'schedule_version_id', versionId),
      queryTable<WbsNode>('schedule_wbs_nodes', 'schedule_version_id', versionId),
      // Fetch raw rows so we can also extract updated_at per row for optimistic concurrency.
      queryTableRawAll('schedule_activities', q => q.eq('schedule_version_id', versionId)),
      queryTable<Relationship>('schedule_relationships', 'schedule_version_id', versionId),
      queryTableAll<Resource>('schedule_resources', q => q),
      queryTable<ResourceAssignment>('schedule_resource_assignments', 'schedule_version_id', versionId),
      queryTable<Baseline>('schedule_baselines', 'schedule_version_id', versionId),
      queryTable<ActivityCodeType>('schedule_activity_code_types', 'schedule_version_id', versionId),
      // code values: fetch all for this version's code types (will filter after)
      queryTableAll<ActivityCodeValue>('schedule_activity_code_values', q => q),
      // all versions for the same project — reuses the shared versionPromise
      versionPromise.then(async v => {
        if (!v) return []
        const rows = await queryTableAll<ScheduleVersion>('schedule_versions', q => q.eq('project_id', v.projectId))
        return normalizeVersions(rows)
      }),
    ])

  if (!version) throw new Error(`Schedule version ${versionId} not found`)

  // Filter code values to only those belonging to this version's code types
  const typeIds = new Set(codeTypes.map(ct => ct.id))
  const filteredCodeValues = codeValues.filter(cv => typeIds.has(cv.codeTypeId))

  // Capture per-row updated_at for optimistic concurrency on save.
  const activityUpdatedAt: Record<string, string> = {}
  for (const r of activitiesResult) {
    if (typeof r.id === 'string' && typeof r.updated_at === 'string') {
      activityUpdatedAt[r.id] = r.updated_at
    }
  }
  const activities = transformRows<Activity>(activitiesResult)

  return {
    version,
    calendars,
    wbs: normalizeWbs(wbs),
    activities: normalizeActivities(activities),
    relationships,
    resources,
    resourceAssignments: assignments,
    baselines,
    activityCodeTypes: codeTypes,
    activityCodeValues: filteredCodeValues,
    versions,
    activityUpdatedAt,
  }
}

async function sbCreateScheduleVersion(version: ScheduleVersion): Promise<ScheduleVersion> {
  const row = {
    id: version.id,
    project_id: version.projectId,
    name: version.name,
    type: version.type,
    status: version.status,
    description: version.description,
    created_from: version.createdFrom,
    created_date: version.createdDate,
    created_by: version.createdBy,
    last_modified_date: version.lastModifiedDate,
    last_modified_by: version.lastModifiedBy,
    data_date: version.dataDate,
    project_start_date: version.projectStartDate ?? version.dataDate,
    locked: version.locked,
    activity_count: version.activityCount,
    relationship_count: version.relationshipCount,
  }
  const { data, error } = await supabase
    .from('schedule_versions')
    .insert(row)
    .select('*')
    .single()
  if (error) throw new Error(`Failed to create schedule version: ${error.message}`)
  const created = normalizeVersion(transformRow<ScheduleVersion>(data as unknown as Record<string, unknown>))

  // First version for a project becomes its default, so a project is never left
  // without a controlling schedule. Later versions have to be promoted by hand.
  if (!created.isDefault) {
    const siblings = await sbGetScheduleVersions(created.projectId)
    const hasDefault = siblings.some((v) => v.isDefault && v.id !== created.id)
    if (!hasDefault) {
      const { error: defErr } = await supabase.rpc('set_default_schedule_version', {
        p_version_id: created.id,
        p_actor: version.createdBy,
      })
      if (!defErr) return { ...created, isDefault: true }
    }
  }
  return created
}

export async function createScheduleVersion(version: ScheduleVersion): Promise<ScheduleVersion> {
  if (!isSupabaseMode()) {
    throw new Error('Creating schedule versions requires Supabase mode (set VITE_DATA_SOURCE=supabase).')
  }
  return sbCreateScheduleVersion(version)
}

async function sbGetScheduleVersions(projectId: string): Promise<ScheduleVersion[]> {
  const { data, error } = await supabase
    .from('schedule_versions')
    .select('*')
    .eq('project_id', projectId)
  if (error) {
    console.warn('[schedule] Error fetching versions:', error.message)
    return []
  }
  return normalizeVersions(transformRows<ScheduleVersion>((data ?? []) as unknown as Record<string, unknown>[]))
}

async function sbGetScheduleDataByProject(projectId: string): Promise<ScheduleData> {
  const versions = await sbGetScheduleVersions(projectId)
  if (versions.length === 0) throw new Error(`No schedule versions found for project ${projectId}`)
  const controlling = resolveDefaultVersion(versions)
  if (!controlling) throw new Error(`No live schedule version found for project ${projectId}`)
  return sbGetScheduleData(controlling.id)
}

// ---------------------------------------------------------------------------
// Public API — delegates to Supabase or mock based on VITE_DATA_SOURCE
// ---------------------------------------------------------------------------

/** Load the full schedule data for a version */
export async function getScheduleData(versionId: string): Promise<ScheduleData> {
  if (isSupabaseMode()) return sbGetScheduleData(versionId)

  const file = resolveScheduleFile(versionId)
  const data = await load<ScheduleData>(file)
  return {
    ...data,
    version: normalizeVersion(data.version),
    versions: normalizeVersions(data.versions ?? []),
    activities: normalizeActivities(data.activities),
    wbs: normalizeWbs(data.wbs),
  }
}

/** Load schedule data by project ID (resolves to primary version) */
export async function getScheduleDataByProject(projectId: string): Promise<ScheduleData> {
  if (isSupabaseMode()) return sbGetScheduleDataByProject(projectId)

  const file = resolveProjectScheduleFile(projectId)
  const data = await load<ScheduleData>(file)
  return {
    ...data,
    version: normalizeVersion(data.version),
    versions: normalizeVersions(data.versions ?? []),
    activities: normalizeActivities(data.activities),
    wbs: normalizeWbs(data.wbs),
  }
}

/** Get all schedule versions for a project */
export async function getScheduleVersions(projectId: string): Promise<ScheduleVersion[]> {
  if (isSupabaseMode()) return sbGetScheduleVersions(projectId)

  const file = resolveProjectScheduleFile(projectId)
  const data = await load<ScheduleData>(file)
  return normalizeVersions(data.versions ?? [])
}

/** Get activities for a schedule version */
export async function getScheduleActivities(versionId: string): Promise<Activity[]> {
  if (isSupabaseMode()) {
    const rows = await queryTable<Activity>('schedule_activities', 'schedule_version_id', versionId)
    return normalizeActivities(rows)
  }

  const file = resolveScheduleFile(versionId)
  const data = await load<ScheduleData>(file)
  return normalizeActivities(data.activities ?? [])
}

/** Get relationships for a schedule version */
export async function getScheduleRelationships(versionId: string): Promise<Relationship[]> {
  if (isSupabaseMode()) return queryTable<Relationship>('schedule_relationships', 'schedule_version_id', versionId)

  const file = resolveScheduleFile(versionId)
  const data = await load<ScheduleData>(file)
  return data.relationships ?? []
}

/** Get WBS tree for a schedule version */
export async function getScheduleWbs(versionId: string): Promise<WbsNode[]> {
  if (isSupabaseMode()) {
    const rows = await queryTable<WbsNode>('schedule_wbs_nodes', 'schedule_version_id', versionId)
    return normalizeWbs(rows)
  }

  const file = resolveScheduleFile(versionId)
  const data = await load<ScheduleData>(file)
  return data.wbs ?? []
}

/** Get calendars for a schedule version */
export async function getScheduleCalendars(versionId: string): Promise<ScheduleCalendar[]> {
  if (isSupabaseMode()) return queryTable<ScheduleCalendar>('schedule_calendars', 'schedule_version_id', versionId)

  const file = resolveScheduleFile(versionId)
  const data = await load<ScheduleData>(file)
  return data.calendars ?? []
}

/** Get resources */
export async function getScheduleResources(): Promise<Resource[]> {
  if (isSupabaseMode()) {
    return queryTableAll<Resource>('schedule_resources', q => q)
  }

  const file = resolveScheduleFile('sv_001')
  const data = await load<ScheduleData>(file)
  return data.resources ?? []
}

/** Get resource assignments for a schedule version */
export async function getScheduleResourceAssignments(versionId: string): Promise<ResourceAssignment[]> {
  if (isSupabaseMode()) return queryTable<ResourceAssignment>('schedule_resource_assignments', 'schedule_version_id', versionId)

  const file = resolveScheduleFile(versionId)
  const data = await load<ScheduleData>(file)
  return data.resourceAssignments ?? []
}

/** Get baselines for a schedule version */
export async function getScheduleBaselines(versionId: string): Promise<Baseline[]> {
  if (isSupabaseMode()) return queryTable<Baseline>('schedule_baselines', 'schedule_version_id', versionId)

  const file = resolveScheduleFile(versionId)
  const data = await load<ScheduleData>(file)
  return data.baselines ?? []
}

/** Get activity code types and values */
export async function getActivityCodes(versionId: string): Promise<{ types: ActivityCodeType[]; values: ActivityCodeValue[] }> {
  if (isSupabaseMode()) {
    const types = await queryTable<ActivityCodeType>('schedule_activity_code_types', 'schedule_version_id', versionId)
    const typeIds = new Set(types.map(t => t.id))
    const allValues = await queryTableAll<ActivityCodeValue>('schedule_activity_code_values', q => q)
    return { types, values: allValues.filter(v => typeIds.has(v.codeTypeId)) }
  }

  const file = resolveScheduleFile(versionId)
  const data = await load<ScheduleData>(file)
  return {
    types: data.activityCodeTypes ?? [],
    values: data.activityCodeValues ?? [],
  }
}

/** Compute schedule statistics from loaded data */
export function computeStatistics(activities: Activity[], relationships: Relationship[], dataDate: string): ScheduleStatistics {
  const incomplete = activities.filter(a => a.status !== 'Completed' && a.activityType !== 'WBSSummary')
  const completed = activities.filter(a => a.status === 'Completed')
  const milestones = activities.filter(a => a.activityType === 'StartMilestone' || a.activityType === 'FinishMilestone')
  const critical = activities.filter(a => a.isCritical && a.status !== 'Completed')

  const allStarts = activities.filter(a => a.earlyStart).map(a => a.earlyStart!)
  const allFinishes = activities.filter(a => a.earlyFinish).map(a => a.earlyFinish!)
  const projectStart = allStarts.length > 0 ? allStarts.sort()[0]! : dataDate
  const projectFinish = allFinishes.length > 0 ? allFinishes.sort().reverse()[0]! : dataDate

  const criticalPathLength = critical.reduce((sum, a) => sum + a.remainingDuration, 0)

  const totalDuration = activities
    .filter(a => a.activityType !== 'SustainedEffort' && a.activityType !== 'WBSSummary')
    .reduce((sum, a) => sum + a.originalDuration, 0)
  const completedDuration = activities
    .filter(a => a.activityType !== 'SustainedEffort' && a.activityType !== 'WBSSummary')
    .reduce((sum, a) => sum + (a.originalDuration * a.percentComplete / 100), 0)
  const percentComplete = totalDuration > 0 ? Math.round(completedDuration / totalDuration * 100) : 0

  const projectTotalFloat = critical.length > 0
    ? Math.min(...critical.map(a => a.totalFloat))
    : 0

  const baselineFinishes = activities.filter(a => a.baselineFinish).map(a => a.baselineFinish!)
  const baselineFinish = baselineFinishes.length > 0 ? baselineFinishes.sort().reverse()[0]! : null

  return {
    totalActivities: activities.length,
    incompleteActivities: incomplete.length,
    completedActivities: completed.length,
    milestones: milestones.length,
    totalRelationships: relationships.length,
    criticalPathLength,
    projectTotalFloat,
    percentComplete,
    dataDate,
    projectStart,
    projectFinish,
    baselineFinish,
  }
}

// ===========================================================================
// Push Scheduling Hierarchy → Schedule WBS
// ===========================================================================
//
// The WBS Hierarchy page's Scheduling tab edits `scheduling_nodes`. The Gantt
// reads from `schedule_wbs_nodes` keyed by `schedule_version_id`. These two
// drift over time, so we need a one-click "Push to Schedule" that diffs them
// and applies the safe portion, with a reconciliation step for the unsafe
// part (deletes of WBS that still have activities pinned underneath).

import type { SchedulingHierarchyNode } from '@/types/hierarchy'

export interface PushDiffAdd {
  source: SchedulingHierarchyNode
}
export interface PushDiffUpdate {
  source: SchedulingHierarchyNode
  target: WbsNode
  // Field-level deltas for the preview UI.
  changes: Array<{ field: string; from: unknown; to: unknown }>
}
export interface PushDiffDelete {
  target: WbsNode
  activityCount: number
  activities: Array<{ id: string; activityId: string; name: string }>
}
/**
 * A WBS element that stays in the schedule but moves under a different parent,
 * with the activities pinned to it. The user decides what happens to those
 * activities (see RehomeChoice).
 */
export interface PushDiffRehome {
  source: SchedulingHierarchyNode
  target: WbsNode
  fromParentCode: string | null
  toParentCode: string | null
  activityCount: number
  activities: Array<{ id: string; activityId: string; name: string }>
}
export interface PushDiff {
  versionId: string
  versionName: string
  adds: PushDiffAdd[]
  updates: PushDiffUpdate[]
  safeDeletes: PushDiffDelete[]  // target WBS not in source AND no activities
  blockedDeletes: PushDiffDelete[] // target WBS not in source AND has activities — requires reconciliation
  /** Updates whose parent changes and that carry activities. Each needs a RehomeChoice. */
  rehomes: PushDiffRehome[]
  /** All WBS codes that will exist after the push — used by the reconcile
   *  UI as the migration-target dropdown options for blocked deletes. */
  resultingWbsByCode: Array<{ code: string; name: string; sourceId: string | null; targetId: string | null }>
}

export interface ReconcileChoice {
  // The blocked-delete target WBS id.
  blockedWbsId: string
  // 'delete-activities' (cascade-delete schedule activities under this WBS),
  // 'migrate' (reassign activities to another WBS by code), or 'keep' (leave
  // this WBS and its activities in the schedule; it is not deleted).
  mode: 'delete-activities' | 'migrate' | 'keep'
  // When mode='migrate', the destination WBS code (must exist after push).
  migrateToCode?: string
}

/** What happens to the activities under a WBS element that moves to a new parent. */
export interface RehomeChoice {
  wbsId: string
  // 'move-with-wbs' (default: activities follow the element to its new place),
  // 'migrate' (reassign them to another WBS by code), 'delete-activities'.
  mode: 'move-with-wbs' | 'migrate' | 'delete-activities'
  migrateToCode?: string
}

export interface PushApplyResult {
  versionId: string
  added: number
  updated: number
  deleted: number
  activitiesDeleted: number
  activitiesMigrated: number
  /** WBS elements that moved to a new parent. */
  rehomed?: number
  /** Blocked deletes the user chose to keep. */
  kept?: number
  errors: string[]
}

/**
 * Pick the schedule version we should push to. Prefer the project's default
 * version when it is writable, then its working (unlocked, non-baseline)
 * version, then the most recently modified unlocked version. Archived versions
 * are never eligible. Returns null if none exists.
 */
async function pickWorkingVersion(projectId: string): Promise<ScheduleVersion | null> {
  const all = await getScheduleVersions(projectId)
  const versions = all.filter((v) => !v.archivedAt)
  if (versions.length === 0) return null
  // The default version is the project's controlling schedule — push there when
  // it can accept writes.
  const preferred = versions.find((v) => v.isDefault && !v.locked)
  if (preferred) return preferred
  // Prefer explicit 'Working' type if present.
  const working = versions.find((v) => v.type === 'Working' && !v.locked)
  if (working) return working
  // Otherwise the latest unlocked version.
  const unlocked = versions.filter((v) => !v.locked)
    .sort((a, b) => (b.lastModifiedDate || '').localeCompare(a.lastModifiedDate || ''))
  return unlocked[0] ?? null
}

/**
 * Auto-push a scheduling-hierarchy description edit to the schedule's WBS.
 *
 * The WBS Manager is the source of truth for WBS text; the schedule's copy in
 * schedule_wbs_nodes is matched BY CODE, exactly like the Push dialog, against
 * the same writable version pickWorkingVersion resolves. Locked and archived
 * versions are never touched. Returns true when at least one schedule WBS row
 * was actually updated.
 */
export async function syncSchedulingDescriptionToSchedule(
  projectId: string,
  code: string,
  description: string,
): Promise<boolean> {
  if (!isSupabaseMode()) return false
  const version = await pickWorkingVersion(projectId)
  if (!version) return false
  const { data, error } = await supabase
    .from('schedule_wbs_nodes')
    .update({ description })
    .eq('schedule_version_id', version.id)
    .eq('code', code)
    .select('id')
  if (error) {
    console.warn('[schedule] Failed to sync WBS description to schedule:', error.message)
    return false
  }
  return (data ?? []).length > 0
}

/** A schedule version a WBS push can write into: not archived, not locked. */
export async function getPushTargetVersions(projectId: string): Promise<{ versions: ScheduleVersion[]; suggestedId: string | null }> {
  const all = (await getScheduleVersions(projectId)).filter((v) => !v.archivedAt)
  const suggested = await pickWorkingVersion(projectId)
  return { versions: all, suggestedId: suggested?.id ?? null }
}

export interface PushDiffOptions {
  /** Push into this version instead of the automatically picked working version. */
  versionId?: string
  /** Preview against a version that will be created empty. */
  emptyTarget?: { name: string }
  /** Allow a locked version as the diff target (preview of a copy only; never written). */
  allowLocked?: boolean
}

/**
 * Build the diff between scheduling-hierarchy nodes (source) and a schedule
 * version's WBS (target). A source node matches a schedule WBS row by the
 * schedule element it is linked to (schedulingWbsNodeId), then by id, then by
 * code, so a renamed code is an update, not a delete plus an add.
 */
export async function buildSchedulingPushDiff(
  source: SchedulingHierarchyNode[],
  projectId: string,
  opts: PushDiffOptions = {},
): Promise<PushDiff | { error: string }> {
  let versionId = ''
  let versionName = ''
  let target: WbsNode[] = []
  let activities: Activity[] = []
  if (opts.emptyTarget) {
    versionName = opts.emptyTarget.name
  } else {
    let version: ScheduleVersion | null
    if (opts.versionId) {
      version = (await getScheduleVersions(projectId)).find((v) => v.id === opts.versionId) ?? null
      if (!version) return { error: 'That schedule version no longer exists.' }
      if (version.archivedAt) return { error: `Schedule version "${version.name}" is archived.` }
    } else {
      version = await pickWorkingVersion(projectId)
    }
    if (!version) {
      return { error: 'No working schedule version found for this project. Open the Scheduling page and create one before pushing.' }
    }
    if (version.locked && !opts.allowLocked) {
      return { error: `Schedule version "${version.name}" is locked. Pushing requires an unlocked working version.` }
    }
    versionId = version.id
    versionName = version.name
    target = await getScheduleWbs(version.id)
    // Pull all activities for the version so we can check what would orphan or move.
    activities = await getScheduleActivities(version.id)
  }
  return computeSchedulingPushDiff(source, target, activities, versionId, versionName)
}

/** Pure half of buildSchedulingPushDiff (exported for tests). */
export function computeSchedulingPushDiff(
  source: SchedulingHierarchyNode[],
  target: WbsNode[],
  activities: Array<Pick<Activity, 'id' | 'activityId' | 'name' | 'wbsId'>>,
  versionId: string,
  versionName: string,
): PushDiff {
  const targetById = new Map(target.map((t) => [t.id, t]))
  const targetByCode = new Map(target.map((t) => [t.code, t]))
  const activitiesByWbsId = new Map<string, Array<{ id: string; activityId: string; name: string }>>()
  for (const a of activities) {
    const list = activitiesByWbsId.get(a.wbsId) ?? []
    list.push({ id: a.id, activityId: a.activityId, name: a.name })
    activitiesByWbsId.set(a.wbsId, list)
  }

  // Match each source node to at most one schedule row, and each row to at most
  // one source node: linked schedule id, then same id, then code.
  const matchOf = new Map<string, WbsNode>()
  const taken = new Set<string>()
  const tryMatch = (s: SchedulingHierarchyNode, t: WbsNode | undefined) => {
    if (!t || taken.has(t.id) || matchOf.has(s.id)) return
    matchOf.set(s.id, t)
    taken.add(t.id)
  }
  for (const s of source) if (s.schedulingWbsNodeId) tryMatch(s, targetById.get(s.schedulingWbsNodeId))
  for (const s of source) tryMatch(s, targetById.get(s.id))
  for (const s of source) tryMatch(s, targetByCode.get(s.code))

  const sourceById = new Map(source.map((s) => [s.id, s]))
  const parentIds = new Set(source.map((s) => s.parentNodeId).filter((p): p is string => !!p))
  // Where a source node's parent will live after the push: its matched row, or
  // (for a new parent) the source id the add is written with.
  const finalIdOfSource = (id: string) => matchOf.get(id)?.id ?? id

  const adds: PushDiffAdd[] = []
  const updates: PushDiffUpdate[] = []
  const rehomes: PushDiffRehome[] = []

  for (const s of source) {
    const t = matchOf.get(s.id)
    if (!t) { adds.push({ source: s }); continue }
    const changes: PushDiffUpdate['changes'] = []
    if (t.code !== s.code) changes.push({ field: 'code', from: t.code, to: s.code })
    if (t.name !== s.name) changes.push({ field: 'name', from: t.name, to: s.name })
    if (t.description !== (s.description ?? '')) changes.push({ field: 'description', from: t.description, to: s.description ?? '' })
    if (t.level !== s.level) changes.push({ field: 'level', from: t.level, to: s.level })
    if (t.sortOrder !== s.sortOrder) changes.push({ field: 'sortOrder', from: t.sortOrder, to: s.sortOrder })
    const projectedType = projectScheduleElementType(s, t, parentIds.has(s.id))
    if (t.elementType !== projectedType) changes.push({ field: 'elementType', from: t.elementType, to: projectedType })
    if ((t.budgetAtCompletion ?? null) !== (s.budgetAtCompletion ?? null)) {
      changes.push({ field: 'budgetAtCompletion', from: t.budgetAtCompletion, to: s.budgetAtCompletion })
    }
    if ((t.responsibleOrg ?? null) !== (s.responsibleOrg ?? null)) {
      changes.push({ field: 'responsibleOrg', from: t.responsibleOrg, to: s.responsibleOrg })
    }
    // Parent: compare where the element sits now with where the source puts it.
    const parentSource = s.parentNodeId ? sourceById.get(s.parentNodeId) : undefined
    const newParentId = parentSource ? finalIdOfSource(parentSource.id) : null
    if ((t.parentId ?? null) !== newParentId) {
      const fromParentCode = t.parentId ? (targetById.get(t.parentId)?.code ?? null) : null
      const toParentCode = parentSource?.code ?? null
      changes.push({ field: 'parent', from: fromParentCode ?? '(top level)', to: toParentCode ?? '(top level)' })
      const acts = activitiesByWbsId.get(t.id) ?? []
      if (acts.length > 0) {
        rehomes.push({ source: s, target: t, fromParentCode, toParentCode, activityCount: acts.length, activities: acts })
      }
    }
    if (changes.length > 0) updates.push({ source: s, target: t, changes })
  }

  // Deletes: schedule rows no source node matched.
  const deletes: PushDiffDelete[] = []
  for (const t of target) {
    if (taken.has(t.id)) continue
    const acts = activitiesByWbsId.get(t.id) ?? []
    deletes.push({ target: t, activityCount: acts.length, activities: acts })
  }

  const safeDeletes = deletes.filter((d) => d.activityCount === 0)
  const blockedDeletes = deletes.filter((d) => d.activityCount > 0)

  // Universe of resulting WBS codes (matched rows under their new code, other
  // kept rows, adds), minus safe deletes. This is the option pool the reconcile
  // UI offers for "migrate activities to".
  const sourceOfTarget = new Map<string, SchedulingHierarchyNode>()
  for (const [sid, t] of matchOf) sourceOfTarget.set(t.id, sourceById.get(sid)!)
  const resultingWbsByCode = new Map<string, { code: string; name: string; sourceId: string | null; targetId: string | null }>()
  const safeIds = new Set(safeDeletes.map((d) => d.target.id))
  for (const t of target) {
    if (safeIds.has(t.id)) continue
    const s = sourceOfTarget.get(t.id)
    const code = s?.code ?? t.code
    resultingWbsByCode.set(code, { code, name: s?.name ?? t.name, sourceId: s?.id ?? null, targetId: t.id })
  }
  for (const a of adds) {
    if (!resultingWbsByCode.has(a.source.code)) {
      resultingWbsByCode.set(a.source.code, { code: a.source.code, name: a.source.name, sourceId: a.source.id, targetId: null })
    }
  }

  return {
    versionId,
    versionName,
    adds,
    updates,
    safeDeletes,
    blockedDeletes,
    rehomes,
    resultingWbsByCode: Array.from(resultingWbsByCode.values()),
  }
}

/**
 * Map a SchedulingHierarchyNode into a schedule_wbs_nodes payload row. The
 * caller chunks these by 500 and upserts via PostgREST.
 */
/**
 * The schedule element type a source node is written with. The schedule has 4
 * types and the WBS Manager hierarchies only say Summary vs element (the
 * Execution WBS even stores every node as a generic element), so: a node with
 * children, or marked Summary, is a Summary; a leaf keeps the type its schedule
 * row already has (Control Account, Planning Package, Work Package); a new
 * leaf is a Work Package.
 */
export function projectScheduleElementType(
  s: SchedulingHierarchyNode,
  existing: Pick<WbsNode, 'elementType'> | null | undefined,
  hasChildren: boolean,
): WbsNode['elementType'] {
  if (hasChildren || s.elementType === 'Summary') return 'Summary'
  if (existing && existing.elementType !== 'Summary') return existing.elementType
  return 'WorkPackage'
}

function schedulingNodeToWbsRow(s: SchedulingHierarchyNode, versionId: string, parentId: string | null, elementType: WbsNode['elementType'] = s.elementType === 'Summary' ? 'Summary' : 'WorkPackage'): Record<string, unknown> {
  return {
    id: s.id,
    schedule_version_id: versionId,
    parent_id: parentId,
    code: s.code,
    name: s.name,
    description: s.description ?? '',
    level: s.level,
    sort_order: s.sortOrder,
    element_type: elementType,
    control_account_id: null,
    responsible_org: s.responsibleOrg ?? null,
    budget_at_completion: s.budgetAtCompletion ?? null,
  }
}

/**
 * Apply the push: insert/update everything in adds+updates, perform safe
 * deletes, and resolve blocked deletes per the user's reconciliation
 * choices (delete-activities OR migrate-to-another-WBS).
 *
 * Throws on RLS or constraint failures; returns a summary on success.
 */
export async function applySchedulingPush(
  diff: PushDiff,
  source: SchedulingHierarchyNode[],
  choices: ReconcileChoice[],
  rehomeChoices: RehomeChoice[] = [],
): Promise<PushApplyResult> {
  const errors: string[] = []
  if (!isSupabaseMode()) {
    return {
      versionId: diff.versionId,
      added: diff.adds.length,
      updated: diff.updates.length,
      deleted: diff.safeDeletes.length + diff.blockedDeletes.length,
      activitiesDeleted: 0,
      activitiesMigrated: 0,
      errors: ['Supabase mode is disabled — push is a no-op in mock mode.'],
    }
  }

  // Build a code→id map for the post-push state. We need this so a
  // 'migrate' choice that targets a new (add) WBS resolves to the
  // correct schedule_wbs_nodes.id.
  const sourceByCode = new Map(source.map((s) => [s.code, s]))
  const upsertById = new Map<string, string>() // code → final schedule_wbs_nodes.id
  const sourceParentIds = new Set(source.map((s) => s.parentNodeId).filter((p): p is string => !!p))

  // adds + updates → upsert payload
  const upserts: Record<string, unknown>[] = []
  for (const a of diff.adds) {
    const parentCode = a.source.parentNodeId
      ? sourceByCode.get(source.find((s) => s.id === a.source.parentNodeId)?.code ?? '')?.code ?? null
      : null
    // parent_id is resolved after we know all rows — for now stash the parentCode in a sidecar map.
    const row = schedulingNodeToWbsRow(a.source, diff.versionId, null,
      projectScheduleElementType(a.source, null, sourceParentIds.has(a.source.id)))
    upserts.push(row)
    upsertById.set(a.source.code, a.source.id)
    void parentCode
  }
  for (const u of diff.updates) {
    const row = schedulingNodeToWbsRow(u.source, diff.versionId, null,
      projectScheduleElementType(u.source, u.target, sourceParentIds.has(u.source.id)))
    // Keep the existing target id so we don't break activity wbsId pointers.
    row.id = u.target.id
    upserts.push(row)
    upsertById.set(u.source.code, u.target.id)
  }

  // Resolve parent_id for each upsert. parentNodeId in the SchedulingHierarchyNode
  // points to ANOTHER SchedulingHierarchyNode.id; the matching schedule row id
  // may be the same (for adds) or the existing target id (for updates).
  const sourceById = new Map(source.map((s) => [s.id, s]))
  // code → final schedule_wbs_nodes.id, covering nodes that ALREADY exist in the
  // schedule (unchanged, or intentionally kept out of THIS push) as well as the
  // adds/updates we're upserting now. Without the existing-node fallback, a node
  // whose parent isn't part of this push — a description-only update on a child,
  // or a selective sync that skips the parent — would lose its parent link and
  // jump to the root. `resultingWbsByCode` carries the existing target ids.
  const codeToFinalId = new Map<string, string>()
  for (const r of diff.resultingWbsByCode) {
    const fid = r.targetId ?? upsertById.get(r.code)
    if (fid) codeToFinalId.set(r.code, fid)
  }
  for (const [code, id] of upsertById) codeToFinalId.set(code, id)
  for (const row of upserts) {
    const code = row.code as string
    const node = sourceByCode.get(code)
    if (!node || !node.parentNodeId) continue
    const parentNode = sourceById.get(node.parentNodeId)
    if (!parentNode) continue
    const parentFinalId = codeToFinalId.get(parentNode.code)
    if (parentFinalId) row.parent_id = parentFinalId
  }

  // Bulk upsert in 500-row chunks.
  for (let i = 0; i < upserts.length; i += 500) {
    const slice = upserts.slice(i, i + 500)
    const { error } = await supabase.from('schedule_wbs_nodes').upsert(slice)
    if (error) errors.push(`Upsert chunk ${Math.floor(i / 500) + 1}: ${error.message}`)
  }

  // Resolve a migration destination code to its post-push schedule row id.
  const destIdForCode = (code: string | undefined): string | null => {
    if (!code) return null
    const dest = diff.resultingWbsByCode.find((r) => r.code === code)
    return dest?.targetId ?? (dest?.sourceId ? upsertById.get(dest.code) ?? null : null)
  }

  let activitiesMigrated = 0
  let activitiesDeleted = 0
  let kept = 0

  // Activities under WBS elements that moved to a new parent: by default they
  // move with the element (nothing to do); otherwise migrate or delete them.
  for (const r of diff.rehomes ?? []) {
    const choice = rehomeChoices.find((c) => c.wbsId === r.target.id)
    if (!choice || choice.mode === 'move-with-wbs') continue
    if (choice.mode === 'migrate') {
      const destId = destIdForCode(choice.migrateToCode)
      if (!destId) {
        errors.push(`Migrate target "${choice.migrateToCode ?? ''}" for WBS "${r.source.code}" not found post-push; its activities moved with it.`)
        continue
      }
      const { error } = await supabase.from('schedule_activities').update({ wbs_id: destId }).eq('wbs_id', r.target.id)
      if (error) { errors.push(`Migrating activities under "${r.source.code}": ${error.message}`); continue }
      activitiesMigrated += r.activityCount
    } else {
      const { error } = await supabase.from('schedule_activities').delete().eq('wbs_id', r.target.id)
      if (error) { errors.push(`Deleting activities under "${r.source.code}": ${error.message}`); continue }
      activitiesDeleted += r.activityCount
    }
  }

  // Apply reconciliation for blocked deletes: keep, migrate activities, or delete them.
  for (const blocked of diff.blockedDeletes) {
    const choice = choices.find((c) => c.blockedWbsId === blocked.target.id)
    if (!choice) {
      errors.push(`No reconcile choice for WBS "${blocked.target.code}" — left in place.`)
      continue
    }
    if (choice.mode === 'keep') {
      kept += 1
      continue
    }
    if (choice.mode === 'migrate') {
      const dest = diff.resultingWbsByCode.find((r) => r.code === choice.migrateToCode)
      const destId = dest?.targetId ?? (dest?.sourceId ? upsertById.get(dest.code) : null)
      if (!destId) {
        errors.push(`Migrate target "${choice.migrateToCode}" for WBS "${blocked.target.code}" not found post-push.`)
        continue
      }
      const { error } = await supabase
        .from('schedule_activities')
        .update({ wbs_id: destId })
        .eq('wbs_id', blocked.target.id)
      if (error) {
        errors.push(`Migrating activities under "${blocked.target.code}": ${error.message}`)
        continue
      }
      activitiesMigrated += blocked.activityCount
    } else {
      // delete-activities: cascade through the activities, then delete the WBS.
      const { error } = await supabase
        .from('schedule_activities')
        .delete()
        .eq('wbs_id', blocked.target.id)
      if (error) {
        errors.push(`Deleting activities under "${blocked.target.code}": ${error.message}`)
        continue
      }
      activitiesDeleted += blocked.activityCount
    }
  }

  // Now delete every WBS row that's gone from the source (safe deletes + the
  // reconciled blocked deletes that the user chose to remove).
  const deleteIds = [
    ...diff.safeDeletes.map((d) => d.target.id),
    ...diff.blockedDeletes
      .filter((b) => {
        const c = choices.find((ch) => ch.blockedWbsId === b.target.id)
        // Migrate => keep deleting the WBS (activities now point elsewhere).
        // Delete-activities => same, delete WBS too. Keep => the WBS stays.
        return c != null && c.mode !== 'keep'
      })
      .map((b) => b.target.id),
  ]
  if (deleteIds.length > 0) {
    const { error } = await supabase.from('schedule_wbs_nodes').delete().in('id', deleteIds)
    if (error) errors.push(`Deleting WBS rows: ${error.message}`)
  }

  return {
    versionId: diff.versionId,
    added: diff.adds.length,
    updated: diff.updates.length,
    deleted: deleteIds.length,
    activitiesDeleted,
    activitiesMigrated,
    rehomed: (diff.rehomes ?? []).length,
    kept,
    errors,
  }
}

export interface PushToNewVersionArgs {
  projectId: string
  source: SchedulingHierarchyNode[]
  name: string
  /** Copy this version first (activities included), or null for an empty version. */
  copyFromVersionId: string | null
  createdBy: string
  /** The diff the user reviewed (against the copy source, or an empty target). */
  previewDiff: PushDiff
  choices: ReconcileChoice[]
  rehomeChoices: RehomeChoice[]
}

/**
 * Create a new schedule version (empty, or a copy of another version), then
 * push the WBS into it. The version is created only here, at apply time, so a
 * cancelled push leaves nothing behind. Choices the user made against the
 * preview are carried over by WBS code, since a copy gets new row ids.
 */
export async function pushToNewScheduleVersion(args: PushToNewVersionArgs): Promise<PushApplyResult> {
  const { projectId, source, name, copyFromVersionId, createdBy, previewDiff } = args
  let created: ScheduleVersion
  if (copyFromVersionId) {
    created = await cloneScheduleVersion(copyFromVersionId, {
      name, type: 'Working', createdBy,
      description: `Copy for a WBS push from WBS Manager`,
    })
  } else {
    const versions = (await getScheduleVersions(projectId)).filter((v) => !v.archivedAt)
    const anchor = versions.find((v) => v.isDefault) ?? versions[0]
    const today = new Date().toISOString().slice(0, 10)
    const nowIso = new Date().toISOString()
    created = await createScheduleVersion({
      id: `sv_${Date.now().toString(36)}`,
      projectId,
      name,
      type: 'Working',
      status: 'Draft',
      description: 'Created from a WBS push from WBS Manager',
      createdFrom: null,
      createdDate: nowIso,
      createdBy,
      lastModifiedDate: nowIso,
      lastModifiedBy: createdBy,
      dataDate: anchor?.dataDate ?? today,
      projectStartDate: anchor?.projectStartDate ?? anchor?.dataDate ?? today,
      locked: false,
      isDefault: false,
      archivedAt: null,
      archivedBy: null,
      activityCount: 0,
      relationshipCount: 0,
    })
  }

  const diff = await buildSchedulingPushDiff(source, projectId, { versionId: created.id })
  if ('error' in diff) throw new Error(`Version "${created.name}" was created, but the push failed: ${diff.error}`)

  // Carry choices over by code: preview row id -> code -> new row id.
  const codeOfPreviewId = new Map<string, string>()
  for (const d of [...previewDiff.blockedDeletes, ...previewDiff.safeDeletes]) codeOfPreviewId.set(d.target.id, d.target.code)
  for (const r of previewDiff.rehomes) codeOfPreviewId.set(r.target.id, r.target.code)
  const newIdOfCode = new Map<string, string>()
  for (const d of [...diff.blockedDeletes, ...diff.safeDeletes]) newIdOfCode.set(d.target.code, d.target.id)
  for (const r of diff.rehomes) newIdOfCode.set(r.target.code, r.target.id)
  const remap = (id: string) => newIdOfCode.get(codeOfPreviewId.get(id) ?? '') ?? id

  const choices = args.choices.map((c) => ({ ...c, blockedWbsId: remap(c.blockedWbsId) }))
  const rehomeChoices = args.rehomeChoices.map((c) => ({ ...c, wbsId: remap(c.wbsId) }))
  // Anything the preview did not show (it should not happen for a fresh copy)
  // is kept rather than deleted.
  for (const b of diff.blockedDeletes) {
    if (!choices.some((c) => c.blockedWbsId === b.target.id)) choices.push({ blockedWbsId: b.target.id, mode: 'keep' })
  }
  return applySchedulingPush(diff, source, choices, rehomeChoices)
}

// ---------------------------------------------------------------------------
// Copy-from-template: clone a template project's schedule (version + WBS +
// activities + relationships) into a target project as a fresh Active version.
// Progress/actuals are reset so the copy is an unstarted schedule; resource
// assignments are NOT copied (resources belong to the source project).
// ---------------------------------------------------------------------------

export interface ScheduleCopyResult {
  copied: boolean
  versionId?: string
  activities: number
  relationships: number
  wbsNodes: number
}

export async function copyScheduleFromProject(
  sourceProjectId: string,
  targetProjectId: string,
  savedByUserId: string,
): Promise<ScheduleCopyResult> {
  if (!isSupabaseMode()) throw new Error('Copying a schedule requires Supabase mode (set VITE_DATA_SOURCE=supabase).')

  const srcVersions = await getScheduleVersions(sourceProjectId)
  if (srcVersions.length === 0) return { copied: false, activities: 0, relationships: 0, wbsNodes: 0 }
  const srcVersion = resolveDefaultVersion(srcVersions) ?? srcVersions[0]!
  const src = await getScheduleData(srcVersion.id)

  // Local, collision-free id generator for the clone.
  let seq = 0
  const stamp = Date.now().toString(36)
  const mkId = (p: string) => `${p}_cp_${stamp}_${(seq++).toString(36)}`

  const nowIso = new Date().toISOString()
  const today = nowIso.slice(0, 10)
  const newVersionId = mkId('sv')

  const targetVersion: ScheduleVersion = {
    id: newVersionId,
    projectId: targetProjectId,
    name: 'Working Schedule',
    // The clone is the project's live schedule, whatever the template's version was.
    type: 'Working',
    status: 'Active',
    description: `Copied from template schedule ${srcVersion.name}`,
    createdFrom: srcVersion.id,
    createdDate: today,
    createdBy: savedByUserId,
    lastModifiedDate: nowIso,
    lastModifiedBy: savedByUserId,
    dataDate: today,
    // Carry the template's planned start so the clone's first CPM run
    // reproduces the template dates rather than snapping to today.
    projectStartDate: srcVersion.projectStartDate ?? today,
    locked: false,
    isDefault: false,
    archivedAt: null,
    archivedBy: null,
    activityCount: 0,
    relationshipCount: 0,
  }
  const createdVersion = await createScheduleVersion(targetVersion)

  // 0) Calendars — a clone with no calendar falls back to a bare 5-day week,
  // so every holiday and shift pattern in the template silently disappears AND
  // the save-time CPM gate (which needs a calendar to run) stops validating.
  // Copy them first so activities can be remapped onto the clone's own ids.
  const calendarIdMap = new Map<string, string>()
  for (const c of src.calendars) calendarIdMap.set(c.id, mkId('cal'))
  const calendarRows = src.calendars.map((c) => ({
    id: calendarIdMap.get(c.id)!,
    schedule_version_id: newVersionId,
    name: c.name,
    type: c.type,
    parent_calendar_id: c.parentCalendarId ? (calendarIdMap.get(c.parentCalendarId) ?? null) : null,
    work_week: c.workWeek,
    exceptions: c.exceptions ?? [],
  }))
  if (calendarRows.length > 0) {
    const { error } = await supabase.from('schedule_calendars').insert(calendarRows)
    if (error) throw new Error(`Failed to copy schedule calendars: ${error.message}`)
  }

  // 1) WBS nodes — remap id + parent, insert into schedule_wbs_nodes.
  const wbsIdMap = new Map<string, string>()
  for (const w of src.wbs) wbsIdMap.set(w.id, mkId('wbs'))
  const wbsRows = src.wbs.map((w) => ({
    id: wbsIdMap.get(w.id)!,
    schedule_version_id: newVersionId,
    parent_id: w.parentId ? (wbsIdMap.get(w.parentId) ?? null) : null,
    code: w.code,
    name: w.name,
    description: w.description ?? '',
    level: w.level,
    sort_order: w.sortOrder,
    element_type: w.elementType,
    control_account_id: null,
    responsible_org: w.responsibleOrg ?? null,
    budget_at_completion: w.budgetAtCompletion ?? null,
  }))
  for (let i = 0; i < wbsRows.length; i += 500) {
    const { error } = await supabase.from('schedule_wbs_nodes').insert(wbsRows.slice(i, i + 500))
    if (error) throw new Error(`Failed to copy schedule WBS nodes: ${error.message}`)
  }

  // 2) Activities — remap id + wbsId, reset progress/actuals for a fresh start.
  const actIdMap = new Map<string, string>()
  for (const a of src.activities) actIdMap.set(a.id, mkId('act'))
  // A source activity can reference a WBS node the source itself no longer
  // has (bad seed, deleted node). Falling back to the SOURCE id would orphan
  // the clone's activity behind a foreign key nothing renders — land those on
  // the clone's root WBS instead, where they stay visible and re-parentable.
  const fallbackWbsId = wbsRows.find((w) => w.parent_id === null)?.id ?? wbsRows[0]?.id
  const activities: Activity[] = src.activities.map((a) => ({
    ...a,
    id: actIdMap.get(a.id)!,
    scheduleVersionId: newVersionId,
    wbsId: wbsIdMap.get(a.wbsId) ?? fallbackWbsId ?? a.wbsId,
    // Point at the clone's own calendar; the source id does not exist here and
    // an unresolvable calendarId silently degrades the task to a 5-day week.
    calendarId: calendarIdMap.get(a.calendarId) ?? calendarRows[0]?.id ?? a.calendarId,
    percentComplete: 0,
    status: 'NotStarted',
    actualStart: null,
    actualFinish: null,
    actualDuration: 0,
    remainingDuration: a.originalDuration,
    // A copy starts unbaselined. Carrying the template's baseline dates made
    // every clone report variance against a baseline the user never set.
    baselineStart: null,
    baselineFinish: null,
  }))

  // 3) Relationships — remap endpoints; drop any whose endpoints didn't copy.
  const relationships: Relationship[] = src.relationships
    .map((r) => {
      const pre = actIdMap.get(r.predecessorActivityId)
      const suc = actIdMap.get(r.successorActivityId)
      if (!pre || !suc) return null
      return { ...r, id: mkId('rel'), scheduleVersionId: newVersionId, predecessorActivityId: pre, successorActivityId: suc }
    })
    .filter((r): r is Relationship => r !== null)

  await saveScheduleVersion({
    versionId: newVersionId,
    expectedLastModifiedDate: createdVersion.lastModifiedDate,
    activities,
    relationships,
    resourceAssignments: [],
    originalActivityIds: [],
    originalRelationshipIds: [],
    originalAssignmentIds: [],
    expectedActivityUpdatedAt: {},
    savedByUserId,
  })

  return { copied: true, versionId: newVersionId, activities: activities.length, relationships: relationships.length, wbsNodes: wbsRows.length }
}

// ---------------------------------------------------------------------------
// Clone a version inside its own project (New Version, Branch, and the working
// copy made when a version is baselined). Unlike copy-from-template this keeps
// progress, actuals and resource assignments: it is the same project's plan,
// so the copy has to pick up exactly where the source left off.
//
// Baseline dates: a locked source IS the baseline, so any activity without
// baseline dates is stamped from the source's current dates. That is what
// makes variance show on the working copy.
// ---------------------------------------------------------------------------

export interface CloneScheduleVersionOptions {
  name: string
  type: ScheduleVersion['type']
  description?: string
  createdBy: string
  /** Submission / Archive copies are born locked; content is written first. */
  locked?: boolean
}

export async function cloneScheduleVersion(
  sourceVersionId: string,
  opts: CloneScheduleVersionOptions,
): Promise<ScheduleVersion> {
  if (!isSupabaseMode()) throw new Error('Copying a schedule version requires Supabase mode (set VITE_DATA_SOURCE=supabase).')

  const src = await getScheduleData(sourceVersionId)
  const srcVersion = src.version
  const stampBaseline = !!srcVersion.locked

  let seq = 0
  const stamp = Date.now().toString(36)
  const mkId = (p: string) => `${p}_cl_${stamp}_${(seq++).toString(36)}`

  const nowIso = new Date().toISOString()
  const newVersionId = mkId('sv')
  const acts = src.activities.filter((a) => a.activityType !== 'WBSSummary')

  const created = await createScheduleVersion({
    id: newVersionId,
    projectId: srcVersion.projectId,
    name: opts.name,
    type: opts.type,
    status: opts.locked ? 'Locked' : 'Draft',
    description: opts.description ?? `Copied from ${srcVersion.name}`,
    createdFrom: srcVersion.id,
    createdDate: nowIso,
    createdBy: opts.createdBy,
    lastModifiedDate: nowIso,
    lastModifiedBy: opts.createdBy,
    dataDate: srcVersion.dataDate,
    projectStartDate: srcVersion.projectStartDate ?? srcVersion.dataDate,
    // Written unlocked so the save RPC accepts the content; locked at the end.
    locked: false,
    isDefault: false,
    archivedAt: null,
    archivedBy: null,
    activityCount: acts.length,
    relationshipCount: src.relationships.length,
  })

  try {
    const calendarIdMap = new Map<string, string>()
    for (const c of src.calendars) calendarIdMap.set(c.id, mkId('cal'))
    const calendarRows = src.calendars.map((c) => ({
      id: calendarIdMap.get(c.id)!,
      schedule_version_id: newVersionId,
      name: c.name,
      type: c.type,
      parent_calendar_id: c.parentCalendarId ? (calendarIdMap.get(c.parentCalendarId) ?? null) : null,
      work_week: c.workWeek,
      exceptions: c.exceptions ?? [],
    }))
    if (calendarRows.length > 0) {
      const { error } = await supabase.from('schedule_calendars').insert(calendarRows)
      if (error) throw new Error(`Failed to copy schedule calendars: ${error.message}`)
    }

    const wbsIdMap = new Map<string, string>()
    for (const w of src.wbs) wbsIdMap.set(w.id, mkId('wbs'))
    const wbsRows = src.wbs.map((w) => ({
      id: wbsIdMap.get(w.id)!,
      schedule_version_id: newVersionId,
      parent_id: w.parentId ? (wbsIdMap.get(w.parentId) ?? null) : null,
      code: w.code,
      name: w.name,
      description: w.description ?? '',
      level: w.level,
      sort_order: w.sortOrder,
      element_type: w.elementType,
      control_account_id: w.controlAccountId ?? null,
      responsible_org: w.responsibleOrg ?? null,
      budget_at_completion: w.budgetAtCompletion ?? null,
    }))
    for (let i = 0; i < wbsRows.length; i += 500) {
      const { error } = await supabase.from('schedule_wbs_nodes').insert(wbsRows.slice(i, i + 500))
      if (error) throw new Error(`Failed to copy schedule WBS nodes: ${error.message}`)
    }

    // Activity codes are version-scoped rows; remap type and value ids.
    const codeTypeIdMap = new Map<string, string>()
    for (const t of src.activityCodeTypes) codeTypeIdMap.set(t.id, mkId('actype'))
    const codeValueIdMap = new Map<string, string>()
    for (const v of src.activityCodeValues) codeValueIdMap.set(v.id, mkId('acval'))
    if (src.activityCodeTypes.length > 0) {
      const { error } = await supabase.from('schedule_activity_code_types').insert(src.activityCodeTypes.map((t) => ({
        id: codeTypeIdMap.get(t.id)!,
        schedule_version_id: newVersionId,
        name: t.name,
        scope: t.scope,
        project_id: t.projectId ?? null,
      })))
      if (error) throw new Error(`Failed to copy activity code types: ${error.message}`)
    }
    const codeValues = src.activityCodeValues.filter((v) => codeTypeIdMap.has(v.codeTypeId))
    if (codeValues.length > 0) {
      const { error } = await supabase.from('schedule_activity_code_values').insert(codeValues.map((v) => ({
        id: codeValueIdMap.get(v.id)!,
        code_type_id: codeTypeIdMap.get(v.codeTypeId)!,
        parent_value_id: v.parentValueId ? (codeValueIdMap.get(v.parentValueId) ?? null) : null,
        code: v.code,
        name: v.name,
        color: v.color,
        sort_order: v.sortOrder,
      })))
      if (error) throw new Error(`Failed to copy activity code values: ${error.message}`)
    }

    const actIdMap = new Map<string, string>()
    for (const a of acts) actIdMap.set(a.id, mkId('act'))
    const fallbackWbsId = wbsRows.find((w) => w.parent_id === null)?.id ?? wbsRows[0]?.id
    const activities: Activity[] = acts.map((a) => ({
      ...a,
      id: actIdMap.get(a.id)!,
      scheduleVersionId: newVersionId,
      wbsId: wbsIdMap.get(a.wbsId) ?? fallbackWbsId ?? a.wbsId,
      calendarId: calendarIdMap.get(a.calendarId) ?? calendarRows[0]?.id ?? a.calendarId,
      activityCodes: (a.activityCodes ?? [])
        .map((ac) => ({ codeTypeId: codeTypeIdMap.get(ac.codeTypeId) ?? '', codeValueId: codeValueIdMap.get(ac.codeValueId) ?? '' }))
        .filter((ac) => ac.codeTypeId && ac.codeValueId),
      baselineStart: a.baselineStart ?? (stampBaseline ? a.earlyStart : null),
      baselineFinish: a.baselineFinish ?? (stampBaseline ? a.earlyFinish : null),
    }))

    const relationships: Relationship[] = src.relationships
      .map((r) => {
        const pre = actIdMap.get(r.predecessorActivityId)
        const suc = actIdMap.get(r.successorActivityId)
        if (!pre || !suc) return null
        return { ...r, id: mkId('rel'), scheduleVersionId: newVersionId, predecessorActivityId: pre, successorActivityId: suc }
      })
      .filter((r): r is Relationship => r !== null)

    const resourceAssignments: ResourceAssignment[] = src.resourceAssignments
      .map((ra) => {
        const act = actIdMap.get(ra.activityId)
        return act ? { ...ra, id: mkId('ra'), activityId: act } : null
      })
      .filter((ra): ra is ResourceAssignment => ra !== null)

    await saveScheduleVersion({
      versionId: newVersionId,
      expectedLastModifiedDate: created.lastModifiedDate,
      activities,
      relationships,
      resourceAssignments,
      originalActivityIds: [],
      originalRelationshipIds: [],
      originalAssignmentIds: [],
      expectedActivityUpdatedAt: {},
      savedByUserId: opts.createdBy,
    })

    if (opts.locked) {
      const { error } = await supabase.from('schedule_versions').update({ locked: true }).eq('id', newVersionId)
      if (error) throw new Error(`Copied, but could not lock the new version: ${error.message}`)
    }
  } catch (e) {
    // Never leave a half-copied version behind looking like a real one.
    await supabase.from('schedule_versions').delete().eq('id', newVersionId)
    throw e
  }

  return { ...created, locked: !!opts.locked }
}
